import { Node } from 'reactflow'
import { BaseNodeData } from '../base/types'
import { NodeTypes } from '../../../../../shared/models/rule-node'
import { EntityType } from '../../../../../shared/models/entity-type.models'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { RenameKeysConfiguration } from './RenameKeysNodeForm'
import { Col, Form, Row } from 'react-bootstrap'
import { Select } from '../../../../../components/page-components/selects-entities/generic-select/Select'
import _ from 'lodash'
import { SelectableValue } from '../../../../../components/page-components/selects-entities/generic-select/selectable-value'
import Input from '../../../../../components/form/Input'
import React from 'react'

export enum NewOriginSearchType {
  CUSTOMER = 'customer',
  TENANT = 'tenant',
  ENTITY_BY_NAME = 'entityByName',
  ENTITY_BY_ID = 'entityByID',
}

interface TransformChangeOriginSettingsEntityByName {
  entityType: EntityType
  namePattern: string
}

export interface TransformChangeOriginConfiguration<
  T extends NewOriginSearchType
> {
  searchType: T
  settings?: T extends NewOriginSearchType.ENTITY_BY_NAME
    ? TransformChangeOriginSettingsEntityByName
    : string
}

interface Props {
  onSubmit: (
    data: Node<
      BaseNodeData<TransformChangeOriginConfiguration<NewOriginSearchType>>,
      NodeTypes.TRANSFORM_CHANGE_ORIGIN
    >
  ) => void
  defaultValues?: Node<
    BaseNodeData<TransformChangeOriginConfiguration<NewOriginSearchType>>,
    NodeTypes.TRANSFORM_CHANGE_ORIGIN
  >
}

const TransformChangeOriginForm = ({ onSubmit, defaultValues }: Props) => {
  const form = useForm<
    Node<
      BaseNodeData<TransformChangeOriginConfiguration<NewOriginSearchType>>,
      NodeTypes.TRANSFORM_CHANGE_ORIGIN
    >
  >({
    defaultValues,
  })
  const options = [
    { label: 'Customer', value: NewOriginSearchType.CUSTOMER },
    { label: 'Tenant', value: NewOriginSearchType.TENANT },
    { label: 'Entity by name', value: NewOriginSearchType.ENTITY_BY_NAME },
  ]
  const searchType = form.watch('data.configuration.searchType')
  return (
    <FormProvider {...form}>
      <Form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        id={'form_rule_node'}
        className={'min-h-100'}
      >
        <Row className={''}>
          <Form.Group as={Col} lg={6} md={12}>
            <Input
              register={form.register('data.name', {
                required: 'Le nom est obligatoire',
              })}
              label={'Nom'}
              error={form.formState.errors?.data?.name}
            />
          </Form.Group>
          <Form.Group
            as={Col}
            lg={6}
            md={12}
            className={'d-flex align-items-center'}
          >
            <Form.Check
              className={'mt-auto'}
              type="switch"
              label="Debug Mode ?"
              {...form.register('data.isDebugMode')}
            />
          </Form.Group>
        </Row>
        <Row className={''}>
          <Form.Group as={Col} lg={6} md={12}>
            <Controller
              name={'data.configuration.searchType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <Select
                    classNamePrefix={'react-select'}
                    options={options}
                    value={value}
                    onChange={(value: SelectableValue) => {
                      onChange(value?.value)
                    }}
                    {..._.omit(form.register('data.configuration.searchType'), [
                      'onChange',
                    ])}
                  />
                )
              }}
            />
          </Form.Group>
        </Row>
        <Row className={'pt-2'}>
          <h3>Settings</h3>
          <Form.Group as={Col} lg={6} md={12}>
            {searchType === NewOriginSearchType.ENTITY_BY_NAME && (
              <>
                <Form.Label htmlFor={'data.configuration.settings.entityType'}>
                  Entity type
                </Form.Label>
                <Controller
                  name={'data.configuration.settings.entityType'}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        classNamePrefix={'react-select'}
                        options={Object.keys(EntityType).map((type) => {
                          return {
                            label: type,
                            value: EntityType[type as keyof typeof EntityType],
                          }
                        })}
                        value={value}
                        onChange={(value: SelectableValue) => {
                          onChange(value?.value)
                        }}
                        {..._.omit(
                          form.register(
                            'data.configuration.settings.entityType',
                            { shouldUnregister: true }
                          ),
                          ['onChange']
                        )}
                      />
                    )
                  }}
                />
              </>
            )}
          </Form.Group>
          <Form.Group as={Col} lg={6} md={12}>
            <Input
              register={form.register(
                'data.configuration.settings.namePattern',
                {
                  required: 'Le nom est obligatoire',
                  shouldUnregister: true,
                }
              )}
              label={'Nom'}
              error={form.formState.errors?.data?.name}
            />
          </Form.Group>
        </Row>
      </Form>
    </FormProvider>
  )
}

export default TransformChangeOriginForm
